/**
 * `prefers-reduced-motion` as a reactive boolean (SKE-4166, phase 2).
 *
 * Tailwind's `motion-safe:` variant already gates the CSS animations, so this
 * hook exists for the decisions CSS cannot make: whether to slide a panel in or
 * snap it into place, and whether to scroll smoothly. It subscribes rather than
 * reading once, so toggling the OS setting mid-session takes effect without a
 * reload — the same contract App.tsx honours for the approval confetti.
 */

import { useSyncExternalStore } from 'react'

const QUERY = '(prefers-reduced-motion: reduce)'

const getMediaQuery = (): MediaQueryList | null => {
  // `lib.dom` types `window.matchMedia` as always present, so this is a
  // `typeof` check rather than a truthiness one — the environments that lack it
  // (jsdom without the stub, any non-browser render) are exactly the ones the
  // type system insists cannot happen.
  const view = globalThis.window as Window | undefined

  return typeof view?.matchMedia === 'function' ? view.matchMedia(QUERY) : null
}

const subscribe = (onChange: () => void): (() => void) => {
  const query = getMediaQuery()

  if (!query) {
    return () => undefined
  }

  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

// jsdom's matchMedia stub reports `false`, which is the right default for a
// preference nobody expressed.
const getSnapshot = (): boolean => getMediaQuery()?.matches ?? false

export const usePrefersReducedMotion = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, () => false)
